import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import { TablePagination, Checkbox, useTheme } from "@material-ui/core";

import { MaskedText, MaskedNumber } from "../../../controls";

import { useCurrencies, useAddCurrency, useUpdateCurrency, useDeleteCurrency } from "../../../services/currencies";
import CurrencySelector from './CurrencySelector';
import { defaultComponents } from '../../../controls/Material-Table-Components';

const CurrencyList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();

  // canEdit should be passed from parent in _props
  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});
  const handleAddCurrency = useAddCurrency();
  const handleUpdateCurrency = useUpdateCurrency();
  const handleDeleteCurrency = useDeleteCurrency();

  const queryClient = useQueryClient();
  const currencies = useCurrencies({tableRef, queryClient});

  const getCurrencyColumns = (props) => { 
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={ 
      "U": "[A-Z]", 
      "A": "[A-Z0-9]", 
      "a": "[A-Za-z0-9]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {
        cellStyle: { padding: "8px 2px 8px 12px" },
        headerStyle: { padding: "8px 2px 8px 12px" },
        title: "Code",
        field: "currencyCode",
        initialEditValue: "",
        editable: props.canEdit ? "onAdd" : "never",
        editComponent: (props) => (
          <MaskedText
            mask="UUUU"
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            forceUpperCase={true}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Name",
        field: "currencyName",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Symbol",
        field: "currencySymbol",
        editComponent: (props) => (
          <MaskedText
            mask={"xxx"}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Child",
        field: "childCurrency",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false, 
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.childCurrency === true} value="true" />        
      },
      {
        ...baseColumn,
        title: "Base Currency",
        field: "baseCurrency",
        editable: (cell,rowData,updateData)=> { return (props.canEdit && updateData.childCurrency === true)},
        editComponent: (props) => (
          <CurrencySelector
            currencies={currencies.data}
            value={props.value}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        title: "Factor",
        field: "offsetFactor",
        width: 100,
        editable: (cell,rowData,updateData)=> { return (props.canEdit && updateData.childCurrency === true)},
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (rowData) => (
          <MaskedNumber
            displayType="text"
            value={rowData.childCurrency === true ? rowData.offsetFactor : undefined}
            decimalScale={0}
            format="######"
          />
        )
      },
      {
        ...baseColumn,
        title: "Favourite",
        field: "favourite",
        width: 70,
        type: "boolean",
        emptyValue: false,
        initialEditValue: false, 
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.favourite === true} value="true" />        
      },
    ];
  };
  
  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 10,
              detailPanelType: "single",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Currency Management"
            columns={getCurrencyColumns({canEdit: state.canEdit})}
            data={currencies.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddCurrency.mutate({record: newData, resolve, reject});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateCurrency.mutate({record: newData, oldRecord: oldData, resolve, reject});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteCurrency.mutate({oldRecord: oldData, resolve, reject});
                  }),
            }}
        />
      </div>
  );

}

export default CurrencyList;
